import { capitalizeFirstLetter } from "@repo/mfe-core";
import {
  type IconKind,
  Icon,
  Typography,
  Chip,
  Box,
  ApplicationIcon,
} from "@saviynt/design-system";
import { useIntl, FormattedMessage } from "react-intl";
import Accordion from "../../../components/Accordion/Accordion";
import {
  TagsCell,
  TextCell,
} from "../../PoliciesPageList/CellRenderer/CellRenderer";
import { getPoliciesColor } from "../../PoliciesPageList/PoliciesPageListColumnConfig";
import { getApplicationSourceIcon } from "../PoliciesSidepanel";
import { useGetPolicyTags } from "../../../hooks/usePolicy";
import LoadErrorWrapper from "../../../components/LoadErrorWrapper/LoadErrorWrapper";
import type { Policy } from "@repo/api/schemas/policies.schema";

export default function SummaryTab({
  policyDetails,
}: {
  policyDetails?: Policy;
}) {
  const intl = useIntl();

  const { data: systemTags, isLoading: isSystemTagsLoading } =
    useGetPolicyTags();

  if (isSystemTagsLoading || !policyDetails) {
    return (
      <Box className="w-full">
        <LoadErrorWrapper isLoading={true} isError={false} />
      </Box>
    );
  }

  const {
    description,
    rule_definition,
    severity,
    metadata: {
      remediation_options,
      exploitation_path = "",
      risk_scenario = "",
    } = {},
  } = policyDetails;
  const { processor, evaluationCriteria, query } = rule_definition;
  const rawEvalCriteria = evaluationCriteria || processor || query || "-";
  const evalCriteria = rawEvalCriteria.replace(/\s*return\b/g, "\nreturn");
  const exploitationPath = exploitation_path || risk_scenario;

  const SectionHeader = ({
    icon,
    message,
  }: {
    icon?: IconKind;
    message: React.ReactNode;
  }) => (
    <div className="flex items-center gap-2">
      {icon && <Icon kind={icon} color="--color-foreground-teal-subtle" />}
      <Typography
        kind="body3"
        desktop="body3-d"
        mobile="body3-d"
        color="--color-foreground-neutral-subtle"
      >
        {message}
      </Typography>
    </div>
  );

  const ChipsList = () => (
    <div className="flex gap-4">
      <Box>
        <Chip
          kind="subtleFilled"
          label={capitalizeFirstLetter(severity ?? "")}
          color={getPoliciesColor(severity)}
          className={severity}
          size="small"
        />
      </Box>
      <TagsCell row={policyDetails} systemTags={systemTags ?? []} />
    </div>
  );

  return (
    <Box className="p-6 space-y-4">
      <ChipsList />

      <div>
        <SectionHeader
          icon="Paragraph"
          message={
            <FormattedMessage
              defaultMessage="Description"
              id="lKiryY"
              description="Description section title"
            />
          }
        />
        <div className="p-2">
          <Typography kind="body2" desktop="body2-d" mobile="body2-d">
            {description}
          </Typography>
        </div>
      </div>

      <div className="rounded-lg gray-bg  p-4">
        <SectionHeader
          message={
            <FormattedMessage
              defaultMessage="Exploitation Path"
              id="GIKvEi"
              description="Exploitation Path title"
            />
          }
        />
        {exploitationPath ? (
          <Typography
            kind="body2"
            desktop="body2-d"
            mobile="body2-d"
            className="pt-2"
          >
            {exploitationPath}
          </Typography>
        ) : (
          <TextCell text="-" />
        )}
      </div>

      <div className="space-y-2">
        <SectionHeader
          icon="Code"
          message={
            <FormattedMessage
              defaultMessage="Evaluation Criteria"
              id="43IcCm"
              description="Evaluation criteria title"
            />
          }
        />
        <div className="rounded-lg gray-bg p-4">
          <Typography
            kind="body2"
            desktop="body2-d"
            mobile="body2-d"
            color="--color-foreground-secondary-bold"
            className="whitespace-pre-wrap"
          >
            {evalCriteria}
          </Typography>
        </div>
      </div>

      <div className="space-y-2">
        <SectionHeader
          icon="Task"
          message={
            <FormattedMessage
              defaultMessage="Remediation"
              id="NJDntP"
              description="Remediation title"
            />
          }
        />

        <div className="space-y-2">
          {remediation_options?.length ? (
            <>
              {remediation_options?.map((option) => (
                <Box key={option.source} className="space-y-2">
                  <Box className="policies-accordion-header-leading-control-area">
                    <ApplicationIcon
                      logo={getApplicationSourceIcon(option.source)}
                      size="xSmall"
                      alt={intl.formatMessage(
                        {
                          defaultMessage: "Applicaton icon for {source}",
                          id: "aS0NML",
                          description: "Application icon",
                        },
                        { source: option.source },
                      )}
                    />
                    <Typography
                      kind="body3"
                      desktop="body3-d"
                      mobile="body3-d"
                      color="--color-foreground-neutral-subtle"
                      className="nhi-accordion-header-text-title"
                    >
                      {option.displayName}
                    </Typography>
                  </Box>
                  <Box className="space-y-2">
                    <Accordion options={option.options} />
                  </Box>
                </Box>
              ))}
            </>
          ) : (
            <TextCell text="-" />
          )}
        </div>
      </div>
    </Box>
  );
}
